import type { TFunction } from "i18next";
import { BookingRequestsStatus } from "../../../components/BookingRequest/typings/booking-requests.types";
import { namePerType } from "../../../components/FacilityManager/Components/Toolbars/InfoSidebar/AssignBox/AssignBox";
import type { UpdateServicesDto } from "../../../hooks/Remote/Services/OrderRequests/useRemoteUpdateBookingServicesByUser";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import {
  ServiceDeliveryStatus,
  ServiceType
} from "../../Booking-Form/typings/booking-service-administration.types";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { PlaceType, ZoneType } from "../../Login/typings/login.types";
import type { BookableBookingObjectDto, ResourceKind } from "../typings/resource-calendar.types";

export const RESOURCE_KINDS: readonly ResourceKind[] = ["equipment", "catering"];

type BuildResourceAvailableTypesArgs = {
  activateBookingServices: boolean;
  standaloneResourceBookingEnabled: boolean;
  placeTypes: PlaceType[];
  workplaceBookingEnabled: boolean;
  zoneTypes: ZoneType[];
};

const hasPlaceType = (placeTypes: PlaceType[], bookingType: BookingType): boolean =>
  placeTypes.some(type => type.name === bookingType);

/**
 * Which place-schedule rows colleague mode may show.
 * Workplace uses company meta; parking/EV use tenant `placeTypes` until per-type enable meta is active.
 */
export type PlaceScheduleTypeVisibility = {
  /** When false, workplace rows from `GET /booking-schedule/place` are hidden. */
  workplaceBookingEnabled: boolean;
  /** Tenant licensed place types. Parking and EV rows require a matching `name`. */
  placeTypes: PlaceType[];
};

/**
 * Whether colleague `GET /booking-schedule/place` should run.
 * Workplace uses company meta; parking/EV use tenant placeTypes only until per-type enable meta is active.
 */
export function isPlaceScheduleFetchEnabled({
  workplaceBookingEnabled,
  placeTypes
}: PlaceScheduleTypeVisibility): boolean {
  return (
    workplaceBookingEnabled ||
    hasPlaceType(placeTypes, BookingType.PARKINGPLACE) ||
    hasPlaceType(placeTypes, BookingType.ELECTRICCHARGINGSTATIONPLACE)
  );
}

/** Keep place-schedule rows that are enabled (workplace meta / parking-EV license). */
export function isPlaceScheduleBookingTypeVisible(
  bookingType: string | undefined,
  { workplaceBookingEnabled, placeTypes }: PlaceScheduleTypeVisibility
): boolean {
  if (bookingType === BookingType.WORKPLACE) {
    return workplaceBookingEnabled;
  }

  if (bookingType === BookingType.PARKINGPLACE) {
    return hasPlaceType(placeTypes, BookingType.PARKINGPLACE);
  }

  if (bookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE) {
    return hasPlaceType(placeTypes, BookingType.ELECTRICCHARGINGSTATIONPLACE);
  }

  return true;
}

export function filterPlaceSchedulesForEnabledTypes(
  schedules: BookingScheduleInterface[] | undefined,
  visibility: PlaceScheduleTypeVisibility
): BookingScheduleInterface[] {
  return (schedules ?? []).filter(schedule =>
    isPlaceScheduleBookingTypeVisible(schedule.bookingType, visibility)
  );
}

export function buildResourceAvailableTypes({
  activateBookingServices,
  standaloneResourceBookingEnabled,
  placeTypes,
  workplaceBookingEnabled,
  zoneTypes
}: BuildResourceAvailableTypesArgs): BookingType[] {
  return [
    ...(workplaceBookingEnabled && hasPlaceType(placeTypes, BookingType.WORKPLACE)
      ? [BookingType.WORKPLACE]
      : []),
    ...(hasPlaceType(placeTypes, BookingType.PARKINGPLACE) ? [BookingType.PARKINGPLACE] : []),
    ...(hasPlaceType(placeTypes, BookingType.ELECTRICCHARGINGSTATIONPLACE)
      ? [BookingType.ELECTRICCHARGINGSTATIONPLACE]
      : []),
    ...(zoneTypes.some(type => type.name === BookingType.CONFERENCEZONE)
      ? [BookingType.CONFERENCEZONE]
      : []),
    ...(hasPlaceType(placeTypes, BookingType.VEHICLE) ? [BookingType.VEHICLE] : []),
    ...(activateBookingServices && standaloneResourceBookingEnabled ? [BookingType.RESOURCE] : [])
  ];
}

export function resolveCatalogLocationInventoryId(
  selectedLocationIds: number[]
): number | undefined {
  if (selectedLocationIds.length !== 1) {
    return undefined;
  }

  return selectedLocationIds[0];
}

export function isResourceCatalogFetchEnabled(resourceBookingType: BookingType | null): boolean {
  return resourceBookingType != null;
}

/** Place/zone/vehicle toggles in resource schedule mode — standalone resource uses kind toggles instead. */
export function resourcePlaceBookingTypes(availableTypes: BookingType[]): BookingType[] {
  return availableTypes.filter(type => type !== BookingType.RESOURCE);
}

export function isStandaloneResourceBookingActive(
  resourceBookingType: BookingType | null
): boolean {
  return resourceBookingType === BookingType.RESOURCE;
}

export function shouldShowResourceKindToggle(
  isResourceMode: boolean,
  resourceAvailableTypes: BookingType[],
  onResourceKindChange?: (kind: ResourceKind) => void
): boolean {
  return (
    isResourceMode &&
    resourceAvailableTypes.includes(BookingType.RESOURCE) &&
    onResourceKindChange != null
  );
}

/** BookingTypeSelector value when standalone resource is active (no place-type button selected). */
export function resourcePlaceBookingTypeToggleValue(
  resourceBookingType: BookingType | null
): BookingType | null {
  return resourceBookingType === BookingType.RESOURCE ? null : resourceBookingType;
}

export function resourceKindLabel(resourceKind: ResourceKind, t: TFunction): string {
  return resourceKind === "equipment" ? t("Equipment") : t("Catering");
}

export function resolveScheduleResourceKind(
  schedule: Pick<BookingScheduleInterface, "resourceKind" | "serviceBookings">
): ResourceKind {
  if (schedule.resourceKind) {
    return schedule.resourceKind;
  }

  const serviceType = schedule.serviceBookings?.[0]?.serviceType;
  if (serviceType === ServiceType.Catering) {
    return "catering";
  }

  return "equipment";
}

/** Ordered quantity for standalone resource bookings (catering line qty / orderedQty).
 * Resource schedule UI currently uses the first service line only (`serviceBookings[0]`).
 */
export function resolveResourceBookingOrderedQty(
  schedule: Pick<BookingScheduleInterface, "serviceBookings">
): number {
  // Resource bookings are modeled as a single service line today.
  const line = schedule.serviceBookings?.[0];
  const qty = line?.orderedQty ?? line?.qty;

  if (qty != null && qty > 0) {
    return qty;
  }

  return 1;
}

export function shouldShowResourceBookingQuantity(
  schedule: Pick<BookingScheduleInterface, "bookingType" | "resourceKind" | "serviceBookings">
): boolean {
  if (schedule.bookingType !== BookingType.RESOURCE) {
    return false;
  }

  return resolveScheduleResourceKind(schedule) === "catering";
}

/** Delivery status for resource booking panel (null when not a resource line). */
export function resolveResourceBookingDeliveryStatus(
  schedule: Pick<BookingScheduleInterface, "bookingType" | "serviceBookings">
): ServiceDeliveryStatus | null {
  if (schedule.bookingType !== BookingType.RESOURCE) {
    return null;
  }

  const line = schedule.serviceBookings?.[0];
  if (line == null) {
    return null;
  }

  return line.deliveryStatus ?? ServiceDeliveryStatus.ORDERED;
}

/** Item approval status for resource booking panel (null when missing). */
export function resolveResourceBookingApprovalStatus(
  schedule: Pick<BookingScheduleInterface, "bookingType" | "serviceBookings">
): BookingRequestsStatus | null {
  if (schedule.bookingType !== BookingType.RESOURCE) {
    return null;
  }

  return schedule.serviceBookings?.[0]?.status ?? null;
}

/** Title-case display for ALL-CAPS enum labels (e.g. APPROVED → Approved). */
export function toTitleCaseStatus(label: string): string {
  if (label.length === 0) {
    return label;
  }
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

export function shouldShowResourceBookingBelowName(
  schedule: Pick<BookingScheduleInterface, "bookingType" | "resourceKind" | "serviceBookings">
): boolean {
  return (
    resolveResourceBookingDeliveryStatus(schedule) != null ||
    resolveResourceBookingApprovalStatus(schedule) != null ||
    shouldShowResourceBookingQuantity(schedule)
  );
}

export function buildResourceBookingQtyUpdateDto(
  schedule: Pick<BookingScheduleInterface, "id" | "serviceBookings">,
  newQty: number
): UpdateServicesDto | null {
  const line = schedule.serviceBookings?.[0];
  if (line == null) {
    return null;
  }

  const status = line.status ?? BookingRequestsStatus.APPROVED;
  const deliveryStatus = line.deliveryStatus ?? ServiceDeliveryStatus.ORDERED;

  return {
    bookingId: String(schedule.id),
    bookingType: BookingType.RESOURCE,
    deliveryStatus,
    servicesInfo: [
      {
        serviceType: line.serviceType as ServiceType,
        status,
        serviceId: line.serviceId,
        setUpTime: line.setUpTime ?? 0,
        qty: newQty,
        orderedQty: newQty,
        availableQty: line.availableQty ?? newQty
      }
    ]
  };
}

export type ScheduleResourceAutocompletePlaceholderKey =
  | "_scheduleResourceAutocomplete_placeholder"
  | "_scheduleResourceAutocomplete_placeholderEquipment"
  | "_scheduleResourceAutocomplete_placeholderCatering"
  | "_scheduleResourceAutocomplete_placeholderWorkplace"
  | "_scheduleResourceAutocomplete_placeholderParking"
  | "_scheduleResourceAutocomplete_placeholderElectricCharging"
  | "_scheduleResourceAutocomplete_placeholderConference"
  | "_scheduleResourceAutocomplete_placeholderVehicle";

export function scheduleResourceAutocompletePlaceholderKey(
  resourceBookingType?: BookingType | null,
  resourceKind?: ResourceKind
): ScheduleResourceAutocompletePlaceholderKey {
  if (resourceBookingType === BookingType.RESOURCE) {
    const kind = resourceKind ?? "equipment";
    return kind === "catering"
      ? "_scheduleResourceAutocomplete_placeholderCatering"
      : "_scheduleResourceAutocomplete_placeholderEquipment";
  }

  if (resourceBookingType === BookingType.WORKPLACE) {
    return "_scheduleResourceAutocomplete_placeholderWorkplace";
  }

  if (resourceBookingType === BookingType.PARKINGPLACE) {
    return "_scheduleResourceAutocomplete_placeholderParking";
  }

  if (resourceBookingType === BookingType.ELECTRICCHARGINGSTATIONPLACE) {
    return "_scheduleResourceAutocomplete_placeholderElectricCharging";
  }

  if (resourceBookingType === BookingType.CONFERENCEZONE) {
    return "_scheduleResourceAutocomplete_placeholderConference";
  }

  if (resourceBookingType === BookingType.VEHICLE) {
    return "_scheduleResourceAutocomplete_placeholderVehicle";
  }

  return "_scheduleResourceAutocomplete_placeholder";
}

export function formatResourceAutocompleteSearchLabel(
  option: BookableBookingObjectDto,
  t: TFunction
): string {
  if (option.bookingType === BookingType.RESOURCE) {
    return [option.locationName, option.floorName, option.objectName].filter(Boolean).join(" - ");
  }

  return [
    t(namePerType(option.bookingType, t)),
    option.locationName,
    option.floorName,
    option.objectName
  ]
    .filter(Boolean)
    .join(" - ");
}

export function matchesBookableBookingObject(
  left: BookableBookingObjectDto,
  right: BookableBookingObjectDto
): boolean {
  if (
    left.bookingInventoryId !== right.bookingInventoryId ||
    left.bookingType !== right.bookingType
  ) {
    return false;
  }

  if (left.bookingType === BookingType.RESOURCE) {
    return left.resourceKind === right.resourceKind;
  }

  return true;
}

export type ResourceCalendarByObjectRequest = {
  bookingInventoryId: number;
  bookingType: BookingType;
  endDate: string;
  startDate: string;
  locationInventoryId?: number;
  resourceKind?: ResourceKind;
};

export function buildResourceCalendarByObjectRequest(
  resource: BookableBookingObjectDto,
  startDate: string,
  endDate: string
): ResourceCalendarByObjectRequest {
  const request: ResourceCalendarByObjectRequest = {
    bookingInventoryId: resource.bookingInventoryId,
    bookingType: resource.bookingType,
    startDate,
    endDate
  };

  if (resource.bookingType !== BookingType.RESOURCE) {
    return request;
  }

  return {
    ...request,
    resourceKind: resource.resourceKind,
    locationInventoryId: resource.locationInventoryId ?? undefined
  };
}
